import React, { useEffect, useState } from 'react'
import { NodeSelection } from '@tiptap/pm/state'
import useStyles from './styles'

const HANDLE_HEIGHT = 24

// Finds the top-level block at the given vertical position.
const findBlockAt = (view, clientY) => {
  const editorRect = view.dom.getBoundingClientRect()
  const paddingLeft = parseFloat(window.getComputedStyle(view.dom).paddingLeft) || 0
  const found = view.posAtCoords({ left: editorRect.left + paddingLeft + 1, top: clientY })
  if (!found) {
    return null
  }
  const $pos = view.state.doc.resolve(found.inside >= 0 ? found.inside : found.pos)
  const pos = $pos.depth > 0 ? $pos.before(1) : $pos.pos
  const node = view.state.doc.nodeAt(pos)
  const dom = node && view.nodeDOM(pos)
  if (!dom || !dom.getBoundingClientRect) {
    return null
  }
  return { pos, node, dom }
}

// Notion-style "+" and "⋮⋮" controls to the left of the hovered block.
const BlockHandle = ({ editor, containerRef }) => {
  const localClasses = useStyles()
  const [block, setBlock] = useState(null)

  useEffect(() => {
    const container = containerRef.current
    const handleMouseMove = (event) => {
      if (event.target.closest && event.target.closest('[data-block-handle]')) {
        return
      }
      const found = findBlockAt(editor.view, event.clientY)
      if (!found) {
        setBlock(null)
        return
      }
      const blockRect = found.dom.getBoundingClientRect()
      const containerRect = container.getBoundingClientRect()
      const lineHeight = parseFloat(window.getComputedStyle(found.dom).lineHeight) || HANDLE_HEIGHT
      const firstLine = Math.min(blockRect.height, lineHeight)
      const top = blockRect.top - containerRect.top + (firstLine - HANDLE_HEIGHT) / 2
      setBlock(current => (current && current.pos === found.pos && current.top === top) ? current : { pos: found.pos, top })
    }
    const handleMouseLeave = () => setBlock(null)
    container.addEventListener('mousemove', handleMouseMove)
    container.addEventListener('mouseleave', handleMouseLeave)
    return () => {
      container.removeEventListener('mousemove', handleMouseMove)
      container.removeEventListener('mouseleave', handleMouseLeave)
    }
  }, [editor, containerRef])

  useEffect(() => {
    // Positions shift after edits; hide until the mouse moves again
    const hide = () => setBlock(null)
    editor.on('update', hide)
    return () => editor.off('update', hide)
  }, [editor])

  if (!block) {
    return null
  }

  const getNode = () => editor.state.doc.nodeAt(block.pos)

  const handleAdd = () => {
    const node = getNode()
    if (!node) {
      return
    }
    if (node.type.name === 'paragraph' && node.content.size === 0) {
      editor.chain().focus().insertContentAt(block.pos + 1, '/').run()
      return
    }
    const after = block.pos + node.nodeSize
    editor.chain()
      .focus()
      .insertContentAt(after, { type: 'paragraph', content: [{ type: 'text', text: '/' }] })
      .setTextSelection(after + 2)
      .run()
  }

  const selectBlock = () => {
    const { view } = editor
    view.dispatch(view.state.tr.setSelection(NodeSelection.create(view.state.doc, block.pos)))
    view.focus()
  }

  const handleDragStart = (event) => {
    const { view } = editor
    selectBlock()
    const slice = view.state.selection.content()
    const { dom, text } = view.serializeForClipboard(slice)
    event.dataTransfer.clearData()
    event.dataTransfer.setData('text/html', dom.innerHTML)
    event.dataTransfer.setData('text/plain', text)
    event.dataTransfer.effectAllowed = 'copyMove'
    const blockDom = view.nodeDOM(block.pos)
    if (blockDom) {
      event.dataTransfer.setDragImage(blockDom, 0, 0)
    }
    view.dragging = { slice, move: true }
  }

  return (
    <div className={localClasses.blockHandle} style={{ top: block.top }} data-block-handle>
      <button
        type='button'
        className={localClasses.handleButton}
        title='Click to add a block below'
        onMouseDown={event => event.preventDefault()}
        onClick={handleAdd}
      >
        +
      </button>
      <div
        className={`${localClasses.handleButton} drag`}
        title='Drag to move, click to select'
        draggable
        onDragStart={handleDragStart}
        onDragEnd={() => setBlock(null)}
        onMouseDown={event => event.stopPropagation()}
        onClick={selectBlock}
      >
        ⋮⋮
      </div>
    </div>
  )
}

export default BlockHandle
